import { Component, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, filter, first } from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { LoaderService } from '@services/loader.service';
import { selectUser, selectUserPreferences } from '@redux/index';
import { setUserPreferences } from '@redux/actions';
import { UserPreferences } from '@shared/_types/UserPreferences';
import { Auth0UserDetailDto, UserManagementService, UserToLinkDto } from '@services/api/api.service';
import { Observable } from 'rxjs';
import { AuthService } from '@auth0/auth0-angular';
import { UserService } from '@services/user.service';
import { LinkAccountModalComponent } from '@components/link-account-modal/link-account-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { getOrganizations } from '@redux/actions';

@Component({
    selector: 'app-portal',
    templateUrl: './portal.component.html',
    styleUrls: ['./portal.component.scss'],
    standalone: false
})
export class PortalComponent implements OnInit {

  appLoaded = false;
  user: Auth0UserDetailDto;
  isGeoviewPage = false;
  isOnboardingPage = false;
  isDataManagementPage = false;
  isInvitePage = false;
  preferences$: Observable<UserPreferences> = this.store.select(selectUserPreferences);

  constructor(
    private readonly store: Store,
    private readonly router: Router,
    private readonly translate: TranslateService,
    private readonly auth: AuthService,
    private readonly dialog: MatDialog,
    private readonly snackbar: MatSnackBar,
    private readonly userService: UserService,
    private readonly userManagementService: UserManagementService,
    private readonly loaderService: LoaderService,
  ) {
    this.translate.addLangs(['en', 'it', 'ja']);
    this.translate.setDefaultLang('en');
  }

  toggleMenu(value: boolean): void {
    this.store.dispatch(setUserPreferences({ value: { sidebar: value } }));
  }

  ngOnInit(): void {
    // Check current URL on init (for page refresh)
    this.checkCurrentRoute(this.router.url);

    this.store.select(selectUser).pipe(
      filter((user) => user != null),
      distinctUntilChanged(),
    ).subscribe(user => {
      this.user = user;
      this.appLoaded = true;
      this.loaderService.hide();
    });

    // FIXME: This should be set at login time
    this.auth.isAuthenticated$.subscribe((isAuthenticated) => {
      if (isAuthenticated) {
        this.auth.user$.pipe(first()).subscribe((userInfo) => {
          this.userService.setUserInfo(userInfo.sub).then(() => {
            this.appLoaded = true;
            this.loaderService.hide();
            const doNotLinkAccount = localStorage.getItem('doNotLinkAccount');
            if (!doNotLinkAccount) {
              this.checkMultipleAccounts();
            }
          }).catch(() => {
            this.loaderService.hide();
          });
        });
      }
    });

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.checkCurrentRoute(event.url);
      }
    });
  }

  private checkCurrentRoute(url: string): void {
    this.isGeoviewPage = url.includes('/geoview') || url.includes('/curfew');
    this.isOnboardingPage = url.includes('/onboarding');
    this.isDataManagementPage = url.includes('/connections');
    this.isInvitePage = /\/invite(\?|$)/.test(url);
  }

  private async checkMultipleAccounts(): Promise<void> {
    this.userManagementService.getUserToLink().subscribe((response: UserToLinkDto) => {
      if (response.hasMultipleAccounts) {
        this.showLinkAccount(response);
      }
    });
  }

  private async showLinkAccount(response: UserToLinkDto): Promise<void> {
    this.dialog.open(LinkAccountModalComponent, {
      data: response,
      disableClose: true,
      panelClass: 'link-account-modal-panel',
      width: '600px',
      maxWidth: '90vw',
    }).afterClosed().subscribe((linkAccount: boolean) => {
      if (linkAccount) {
        this.userManagementService.linkAccounts({
          primaryAccountId: response.primaryAccount.userId,
          secondaryAccountId: response.secondaryAccount.userId,
        }).subscribe({
          next: () => {
            this.snackbar.open('Accounts linked successfully', 'Close', { duration: 3000 });
            this.store.dispatch(getOrganizations());
          },
          error: () => {
            this.snackbar.open('Failed to link accounts', 'Close', { duration: 3000 });
          },
        });
      } else {
        localStorage.setItem('doNotLinkAccount', 'true');
      }
    });
  }
}
